import { useState } from 'react';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { C, ink } from '../lib/theme';
import { ScaleModal, type Origin } from './ScaleModal';
import { IconCheck, IconCopy, IconMail, IconPhone } from './icons';
import { ErrorBanner, Skeleton } from './primitives';

/**
 * Modal de Contato (PRD §7.5) — Scale Modal nível 2. Diferente do Client Drawer:
 * só contato, rápido de abrir e fechar, pensado pra checagem repetida dezenas de
 * vezes por dia. O rodapé faz a transição consciente nível 2 → nível 3.
 */
export function ContactModal({ masterId, origin, onClose, onOpenClient }: {
  masterId: string | null;
  origin: Origin | null;
  onClose: () => void;
  onOpenClient: (masterId: string) => void;
}) {
  const { data, loading, error, reload } = useAsync(
    () => (masterId ? api.clientContacts(masterId) : Promise.resolve([])),
    [masterId],
  );
  const [copied, setCopied] = useState<string | null>(null);

  async function copy(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(value);
      setTimeout(() => setCopied((c) => (c === value ? null : c)), 1600);
    } catch {
      setCopied(null);
    }
  }

  const phones = (data ?? []).filter((c) => c.contact_type?.toLowerCase().includes('phone') || c.contact_type?.toLowerCase().includes('tel'));
  const emails = (data ?? []).filter((c) => !phones.includes(c));

  return (
    <ScaleModal
      open={masterId !== null} origin={origin} onClose={onClose}
      title={masterId ? `Contatos — ${masterId}` : 'Contatos'} width={400}
    >
      {loading && <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>{[0, 1, 2].map((i) => <Skeleton key={i} height={38} radius={10} />)}</div>}
      {error && <ErrorBanner message={error} onRetry={reload} />}

      {!loading && !error && (data?.length ?? 0) === 0 && (
        <div style={{ fontSize: 12.5, color: ink(0.5), padding: '18px 4px', textAlign: 'center' }}>
          Nenhum telefone ou email registrado para este cliente.
        </div>
      )}

      {!loading && !error && (data?.length ?? 0) > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {[
            { label: 'Telefones', rows: phones, tel: true, Icon: IconPhone },
            { label: 'Emails', rows: emails, tel: false, Icon: IconMail },
          ]
            .filter((g) => g.rows.length > 0)
            .map((g) => (
              <div key={g.label}>
                {/* Ícone chamativo em vez do rótulo em caixa alta — mesmo
                    padrão do "Anexos" do EvidenceModal. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
                  <span style={{
                    width: 24, height: 24, borderRadius: 8, flex: 'none', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(255,144,112,0.14)', color: C.accent,
                  }}><g.Icon size={12} /></span>
                  <span style={{ fontSize: 12.5, fontWeight: 750, color: ink(0.8) }}>{g.label}</span>
                  <span style={{
                    fontSize: 10.5, fontWeight: 800, padding: '1px 7px', borderRadius: 99,
                    background: ink(0.07), color: ink(0.45), fontVariantNumeric: 'tabular-nums',
                  }}>{g.rows.length}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {g.rows.map((c, i) => (
                    <div key={`${c.value}-${i}`} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                      borderRadius: 10, background: ink(0.05), border: `1px solid ${ink(0.09)}`,
                    }}>
                      <span style={{
                        flex: 1, minWidth: 0, fontSize: 12.5, fontVariantNumeric: 'tabular-nums',
                        color: ink(0.88), wordBreak: 'break-all',
                      }}>{c.value}</span>
                      <button type="button" onClick={() => copy(c.value)} className="ko-ghost" style={{
                        border: `1px solid ${ink(0.13)}`, background: ink(0.05),
                        color: copied === c.value ? C.green : ink(0.62), cursor: 'pointer', font: 'inherit',
                        fontSize: 11, fontWeight: 650, padding: '4px 8px', borderRadius: 8, flex: 'none',
                      }}>{copied === c.value ? <><IconCheck size={10} />copiado</> : <><IconCopy size={10} />copiar</>}</button>
                      {g.tel && (
                        <a href={`tel:${c.value.replace(/[^\d+]/g, '')}`} className="ko-ghost" style={{
                          border: `1px solid ${ink(0.13)}`, background: ink(0.05), color: ink(0.62),
                          font: 'inherit', fontSize: 11, fontWeight: 650, padding: '4px 8px',
                          borderRadius: 8, flex: 'none', textDecoration: 'none',
                        }}>ligar</a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      )}

      <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${ink(0.08)}` }}>
        <button
          type="button"
          onClick={() => { if (masterId) { onClose(); onOpenClient(masterId); } }}
          style={{
            border: 'none', background: 'none', padding: 0, cursor: 'pointer', font: 'inherit',
            fontSize: 12.5, fontWeight: 650, color: C.accent,
          }}
        >Ver perfil completo do cliente →</button>
      </div>
    </ScaleModal>
  );
}
